/**
 * ConduitePage - Tableau de bord de la conduite éditoriale
 * 
 * Avec support Drag & Drop:
 * - Sites réordonnables dans chaque section
 * - Articles déplaçables entre sites et vers week-end
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ExternalLink, 
  Clock, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  FileText,
  Users,
  Calendar,
  Clipboard,
  RefreshCw,
  Settings,
  Save,
  X,
  ChevronUp,
  ChevronDown,
  Rss,
  Building2,
  Star,
  CalendarDays,
  GripVertical,
  Plus,
  Trash2,
  MinusCircle,
  Bell,
  Check,
  Feather,
  History,
  Link,
  Edit2,
  Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import HourlyAlertPopup from '../components/HourlyAlertPopup';
import AbsencesPanel from '../components/AbsencesPanel';

// DnD Kit imports
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs des sites - thème navy unifié
const SITE_COLORS = {
  'economiematin': { bg: 'bg-slate-700', text: 'text-white' },
  'ecodigest': { bg: 'bg-slate-700', text: 'text-white' },
  'politiquematin': { bg: 'bg-slate-700', text: 'text-white' },
  'santematin': { bg: 'bg-slate-700', text: 'text-white' },
  'ecoquick': { bg: 'bg-slate-700', text: 'text-white' },
  'jde': { bg: 'bg-slate-700', text: 'text-white' },
  'nlto': { bg: 'bg-slate-700', text: 'text-white' },
  'enderi': { bg: 'bg-slate-700', text: 'text-white' },
  'rsemagazine': { bg: 'bg-slate-700', text: 'text-white' },
  'carnetsdubusiness': { bg: 'bg-slate-700', text: 'text-white' },
  'speedylife': { bg: 'bg-slate-700', text: 'text-white' },
  'greenvert': { bg: 'bg-slate-700', text: 'text-white' },
  'socialmag': { bg: 'bg-slate-700', text: 'text-white' },
  'armees': { bg: 'bg-slate-700', text: 'text-white' },
  'juriguide': { bg: 'bg-slate-700', text: 'text-white' },
  'automobiliste': { bg: 'bg-slate-700', text: 'text-white' },
  'afriquematin': { bg: 'bg-slate-700', text: 'text-white' },
  'energeek': { bg: 'bg-slate-700', text: 'text-white' },
  'consoglobe': { bg: 'bg-slate-700', text: 'text-white' },
  'default': { bg: 'bg-slate-700', text: 'text-white' }
};

const SITE_CATEGORIES = [
  { value: 'groupe', label: 'Groupe', icon: Building2, color: 'text-sky-400' },
  { value: 'va', label: 'VA', icon: Star, color: 'text-slate-300' },
  { value: 'autre', label: 'Autre', icon: FileText, color: 'text-slate-400' }
];

const getSiteColors = (siteName) => {
  const key = siteName?.toLowerCase().replace(/[^a-z]/g, '') || 'default';
  return SITE_COLORS[key] || SITE_COLORS.default;
};

const getFaviconUrl = (siteUrl) => {
  try {
    const url = new URL(siteUrl);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=32`;
  } catch {
    return null;
  }
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// ==================== DRAGGABLE ARTICLE ROW ====================
const DraggableArticleRow = ({ pub, siteId, onDelete, onStatusChange }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ 
    id: `article-${pub.id}`,
    data: { 
      type: 'article', 
      article: pub,
      sourceSiteId: siteId,
      isWeekend: pub.is_weekend || false
    }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };
  
  // Toggle entre publié et brouillon
  const handleToggleStatus = async () => {
    setUpdatingStatus(true);
    try {
      const newStatus = pub.publish_status === 'publish' ? 'draft' : 'publish';
      await onStatusChange(pub.id, newStatus, pub.title);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Déterminer l'apparence du bouton selon le statut
  const getStatusButton = () => {
    const isPublished = pub.publish_status === 'publish';
    const isForgotten = pub.publish_status === 'forgotten';
    
    return (
      <button
        onClick={handleToggleStatus}
        disabled={updatingStatus}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-all shadow-sm ${
          isPublished 
            ? 'bg-emerald-500 hover:bg-amber-500 text-white' 
            : isForgotten
              ? 'bg-red-500 hover:bg-emerald-500 text-white'
              : 'bg-amber-500 hover:bg-emerald-500 text-white'
        }`}
        title={
          isPublished 
            ? 'Repasser en brouillon' 
            : `Marquer comme publié (actuellement: ${isForgotten ? 'oublié' : 'brouillon'})`
        }
      >
        {updatingStatus ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : isPublished ? (
          <CheckCircle2 className="w-3 h-3" />
        ) : (
          <CheckCircle2 className="w-3 h-3" />
        )}
      </button>
    );
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-t border-slate-800/50 ${isDragging ? 'bg-sky-900/30 shadow-lg' : 'hover:bg-slate-800/30'} group`}
    >
      <td className="py-1.5 w-6 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="w-3 h-3 text-slate-500 hover:text-sky-400" />
      </td>
      <td className="py-1.5 text-slate-200 truncate max-w-[120px]" title={pub.title}>
        {pub.published_url ? (
          <a 
            href={pub.published_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-sky-400 hover:underline flex items-center gap-1"
            title={`Voir l'article en ligne: ${pub.title}`}
          >
            {pub.title?.slice(0, 28)}{pub.title?.length > 28 ? '...' : ''}
            <ExternalLink className="w-3 h-3 text-sky-400 opacity-75" />
          </a>
        ) : (
          <span>{pub.title?.slice(0, 30)}{pub.title?.length > 30 ? '...' : ''}</span>
        )}
      </td>
      <td className="py-1.5">
        <span className="truncate max-w-[80px] text-[11px] text-white" title={pub.author_name || pub.user_name}>
          {pub.author_name || pub.user_name || '-'}
        </span>
      </td>
      <td className="py-1.5 text-center">
        {getStatusButton()}
      </td>
      <td className="py-1.5 w-8">
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogTrigger asChild>
            <button 
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/30 text-slate-500 hover:text-red-400 transition-all"
              title="Supprimer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Supprimer cet article ?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Vous êtes sur le point de supprimer <span className="font-semibold text-sky-400">"{pub.title?.slice(0, 50)}{pub.title?.length > 50 ? '...' : ''}"</span> de la conduite éditoriale.
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => onDelete(pub.id, pub.title)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
};

// ==================== ARTICLE DROP ZONE ====================
const ArticleDropZone = ({ id, children, label, siteId, isWeekend = false }) => {
  const { setNodeRef, isOver, active } = useDroppable({ 
    id,
    data: {
      type: 'dropzone',
      siteId,
      isWeekend
    }
  });
  
  // Only show drop indicator when dragging an article
  const showDropIndicator = isOver && active?.id?.toString().startsWith('article-');
  
  return (
    <div
      ref={setNodeRef}
      data-dropzone-id={id}
      className={`min-h-[50px] rounded-lg transition-all duration-200 relative ${
        showDropIndicator 
          ? isWeekend 
            ? 'bg-sky-500/20 ring-2 ring-sky-400 ring-dashed' 
            : 'bg-sky-500/30 ring-2 ring-sky-500 ring-dashed'
          : ''
      }`}
    >
      {children}
      {showDropIndicator && (
        <div className={`absolute inset-0 flex items-center justify-center rounded-lg ${
          isWeekend ? 'bg-slate-700/50' : 'bg-slate-800/50'
        }`}>
          <div className={`text-center py-2 px-4 rounded-lg ${
            isWeekend 
              ? 'bg-slate-700 text-sky-300 border border-sky-500/50' 
              : 'bg-sky-900/90 text-sky-300 border border-sky-500'
          } text-xs font-medium animate-pulse shadow-lg`}>
            ↓ Déposer ici → {label} {isWeekend ? '(Week-end)' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SORTABLE SITE CARD ====================
const SortableSiteCard = ({ site, publications, weekendPublications, cpPublications, siteCategory, isAmiral, onSiteUpdate, onRemoveFromConduite, onDeleteArticle, onAddArticle, onStatusChange, onRefreshRss, activeArticleId, allSites }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `site-${site.id}`, disabled: !isAmiral });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const colors = getSiteColors(site.name);
  // Exclude weekend articles and CP from today's list (tribunes are shown in separate section)
  const todayPubs = publications.filter(p => p.site_id === site.id && !p.is_weekend && !p.is_cp && !p.is_tribune);
  const weekendPubs = weekendPublications?.filter(p => p.site_id === site.id) || [];
  const cpPubs = cpPublications?.filter(p => p.site_id === site.id) || [];
  const [isEditing, setIsEditing] = useState(false);
  const [editObjective, setEditObjective] = useState(site.daily_objective || 2);
  const [editStrict, setEditStrict] = useState(site.strict_objective || false);
  const [editCategory, setEditCategory] = useState(site.site_category || 'autre');
  const [editRssFeed, setEditRssFeed] = useState(site.rss_feed_url || '');
  const [useWeeklyObjectives, setUseWeeklyObjectives] = useState(site.use_weekly_objectives || false);
  const defaultWeekly = { lundi: 2, mardi: 2, mercredi: 2, jeudi: 2, vendredi: 2, samedi: 0, dimanche: 0 };
  const [weeklyObjectives, setWeeklyObjectives] = useState({
    ...defaultWeekly,
    ...(site.weekly_objectives || {})
  });
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [newArticleTitle, setNewArticleTitle] = useState('');
  const [newArticleAuthor, setNewArticleAuthor] = useState('');
  const [newArticleIsWeekend, setNewArticleIsWeekend] = useState(false);
  const [addingArticle, setAddingArticle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshingRss, setRefreshingRss] = useState(false);
  
  // Rafraîchir le RSS pour ce site uniquement
  const handleRefreshRss = async () => {
    if (!site.rss_feed_url) {
      toast.error('Pas de flux RSS configuré pour ce site');
      return;
    }
    setRefreshingRss(true);
    try {
      const result = await onRefreshRss(site.id);
      if (result?.error) {
        toast.error(result.error);
      } else if (result?.publications_confirmed > 0) {
        toast.success(`${result.publications_confirmed} article(s) confirmé(s) pour ${site.name}`);
      } else {
        toast.info(`Aucun nouvel article confirmé pour ${site.name}`);
      }
    } catch (err) {
      toast.error('Erreur lors de la vérification RSS');
    } finally {
      setRefreshingRss(false);
    }
  };
  
  const handleAddArticle = async () => {
    if (!newArticleTitle.trim()) return;
    setAddingArticle(true);
    try {
      await onAddArticle(newArticleTitle.trim(), site.id, newArticleAuthor.trim(), newArticleIsWeekend);
      setNewArticleTitle('');
      setNewArticleAuthor('');
      setNewArticleIsWeekend(false);
      setShowAddArticle(false);
    } finally {
      setAddingArticle(false);
    }
  };
  
  const published = todayPubs.filter(p => p.publish_status === 'publish').length;
  const inProgress = todayPubs.filter(p => p.publish_status === 'draft' || p.publish_status === 'pending').length;
  const forgotten = todayPubs.filter(p => p.publish_status === 'forgotten').length;
  const objective = site.daily_objective || 2;
  const isStrict = site.strict_objective || false;
  const remaining = Math.max(0, objective - published - inProgress - forgotten);
  const isGroupe = siteCategory === 'groupe';
  
  // Check if an article is being dragged over this site
  const isArticleDropTarget = activeArticleId && !isDragging;
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (editObjective !== objective) params.append('daily_objective', editObjective);
      if (editStrict !== isStrict) params.append('strict_objective', editStrict);
      if (editCategory !== (site.site_category || 'autre')) params.append('site_category', editCategory);
      if (editRssFeed !== (site.rss_feed_url || '')) params.append('rss_feed_url', editRssFeed);
      
      // Gestion des objectifs hebdomadaires
      if (useWeeklyObjectives !== (site.use_weekly_objectives || false)) {
        params.append('use_weekly_objectives', useWeeklyObjectives);
      }
      if (useWeeklyObjectives) {
        params.append('weekly_objectives', JSON.stringify(weeklyObjectives));
      }
      
      if (params.toString()) {
        await axios.patch(`${API}/api/wordpress-sites/${site.id}/conduite?${params.toString()}`);
        toast.success(`Site "${site.name}" mis à jour`);
        onSiteUpdate(site.id, {
          daily_objective: editObjective,
          strict_objective: editStrict,
          site_category: editCategory,
          rss_feed_url: editRssFeed,
          use_weekly_objectives: useWeeklyObjectives,
          weekly_objectives: weeklyObjectives
        });
      }
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditObjective(objective);
    setEditStrict(isStrict);
    setEditCategory(site.site_category || 'autre');
    setEditRssFeed(site.rss_feed_url || '');
    setUseWeeklyObjectives(site.use_weekly_objectives || false);
    const defaultWeekly = { lundi: 2, mardi: 2, mercredi: 2, jeudi: 2, vendredi: 2, samedi: 0, dimanche: 0 };
    setWeeklyObjectives({
      ...defaultWeekly,
      ...(site.weekly_objectives || {})
    });
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`overflow-hidden border border-slate-700/50 shadow-lg bg-slate-900/90 ${isDragging ? 'ring-2 ring-sky-500' : ''}`}
    >
      {/* Header avec drag handle */}
      <div className="bg-slate-800 text-white px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
            {/* Drag handle for site - only for Amiraux */}
            {isAmiral && (
              <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-white/20 rounded flex-shrink-0" title="Glisser pour réordonner">
                <GripVertical className="w-4 h-4 text-white/80" />
              </div>
            )}
            {site.url && (
              <img 
                src={getFaviconUrl(site.url)} 
                alt="" 
                className="w-4 h-4 rounded bg-white p-0.5 flex-shrink-0"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <h3 className="font-bold text-sm uppercase tracking-wide text-white truncate">{site.name}</h3>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {site.rss_feed_url && (
              <>
                <button 
                  onClick={handleRefreshRss}
                  disabled={refreshingRss}
                  className={`p-1 rounded bg-white/10 text-white hover:bg-emerald-500/50 transition-colors ${refreshingRss ? 'opacity-50' : ''}`}
                  title="Vérifier le flux RSS"
                >
                  {refreshingRss ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                </button>
                <a href={site.rss_feed_url} target="_blank" rel="noopener noreferrer" className="p-1 rounded bg-white/10 text-white hover:bg-white/20" title="Ouvrir le flux RSS">
                  <Rss className="w-3.5 h-3.5" />
                </a>
              </>
            )}
            {isAmiral && (
              <>
                <button onClick={() => setIsEditing(!isEditing)} className="p-1 rounded bg-white/10 text-white hover:bg-sky-500/50" title="Configurer">
                  <Settings className="w-3.5 h-3.5" />
                </button>
                <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
                  <AlertDialogTrigger asChild>
                    <button className="p-1 rounded bg-white/10 text-white hover:bg-red-500/50 hover:text-red-200" title="Retirer de la conduite">
                      <MinusCircle className="w-3.5 h-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Retirer ce site de la conduite ?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        Vous êtes sur le point de retirer <span className="font-semibold text-sky-400">{site.name}</span> de la conduite éditoriale. 
                        Le site ne sera plus visible dans ce tableau de bord mais pourra être réajouté ultérieurement.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => {
                          onRemoveFromConduite(site.id, site.name);
                          setShowRemoveConfirm(false);
                        }}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        Retirer de la conduite
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            {site.url && (
              <a href={site.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded bg-white/10 text-white hover:bg-white/20" title="Ouvrir le site">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
      
      <CardContent className="p-3 space-y-3">
        {isEditing ? (
          <div className="p-3 bg-slate-800/50 rounded-lg border border-sky-500/30 space-y-3">
            <div className="space-y-2">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Catégorie</label>
              <div className="flex gap-2">
                {SITE_CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setEditCategory(cat.value)}
                    disabled={saving}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all border ${
                      editCategory === cat.value 
                        ? 'bg-sky-500 text-white border-sky-400 shadow-md' 
                        : 'bg-slate-500 text-white border-slate-400 hover:bg-slate-400'
                    }`}
                  >
                    <cat.icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Objectif</label>
              {!useWeeklyObjectives ? (
                <>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditObjective(prev => Math.max(0, prev - 1))} className="p-1.5 rounded bg-slate-500 hover:bg-slate-400 text-white font-bold" disabled={saving}>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <Input
                      type="number" min="0" max="50" value={editObjective}
                      onChange={(e) => setEditObjective(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                      className="w-14 h-8 text-center text-sm bg-slate-800 border-slate-500 text-white font-semibold" disabled={saving}
                    />
                    <button onClick={() => setEditObjective(prev => Math.min(50, prev + 1))} className="p-1.5 rounded bg-slate-500 hover:bg-slate-400 text-white font-bold" disabled={saving}>
                      <ChevronUp className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-xs text-slate-300">/ jour</span>
                </>
              ) : (
                <span className="text-xs text-slate-400 italic">Défini par jour ci-dessous</span>
              )}
              <label className="flex items-center gap-2 ml-4 cursor-pointer">
                <input type="checkbox" checked={editStrict} onChange={(e) => setEditStrict(e.target.checked)} disabled={saving}
                  className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-rose-500" />
                <span className="text-xs text-slate-300 font-medium">Strict</span>
              </label>
            </div>
            
            {/* Toggle production par jour */}
            <div className="flex items-center gap-3 pt-2 border-t border-slate-600/30">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={useWeeklyObjectives} 
                  onChange={(e) => setUseWeeklyObjectives(e.target.checked)} 
                  disabled={saving}
                  className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-amber-500" 
                />
                <span className="text-xs text-slate-300 font-medium">Production variable par jour</span>
              </label>
              {useWeeklyObjectives && (
                <span className="text-[10px] text-amber-400/70 italic">L'objectif change selon le jour de la semaine</span>
              )}
            </div>
            
            {/* Grille des objectifs par jour de la semaine */}
            {useWeeklyObjectives && (
              <div className="grid grid-cols-7 gap-1 pt-2">
                {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day, idx) => {
                  const dayValue = weeklyObjectives[day] !== undefined ? weeklyObjectives[day] : (idx >= 5 ? 0 : 2);
                  return (
                    <div key={day} className="flex flex-col items-center gap-1">
                      <span className={`text-[9px] uppercase font-medium ${idx >= 5 ? 'text-violet-400' : 'text-slate-400'}`}>
                        {day.slice(0, 3)}
                      </span>
                      <input
                        type="number" min="0" max="50"
                        value={dayValue}
                        onChange={(e) => setWeeklyObjectives(prev => ({
                          ...prev,
                          [day]: Math.max(0, Math.min(50, parseInt(e.target.value) || 0))
                        }))}
                        className={`w-10 h-7 text-center text-xs bg-slate-800 border border-slate-500 text-white font-semibold rounded ${idx >= 5 ? 'border-violet-500/50' : ''}`}
                        disabled={saving}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="space-y-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1">
                <Rss className="w-3 h-3" /> Flux RSS
              </label>
              <Input type="url" value={editRssFeed} onChange={(e) => setEditRssFeed(e.target.value)}
                placeholder="https://example.com/feed.xml" className="h-8 text-sm bg-slate-800 border-slate-500 text-white placeholder:text-slate-500" disabled={saving} />
            </div>
            
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-600/50">
              <button onClick={handleCancel} disabled={saving} className="px-4 py-2 rounded bg-slate-500 hover:bg-slate-400 text-white text-xs font-semibold flex items-center gap-1.5">
                <X className="w-3.5 h-3.5" /> Annuler
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold flex items-center gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">
                Objectif : <strong className="text-slate-200">{site.published_today || 0}/{objective}</strong>
                {isStrict && <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-400 text-[10px] font-semibold">STRICT</span>}
                {site.objective_completed && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-semibold">Complété</span>}
              </span>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" title="Publiés">
                <CheckCircle2 className="w-3 h-3" /> {published}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30" title="Rédigés (pas en ligne)">
                <Loader2 className="w-3 h-3" /> {inProgress}
              </span>
              {forgotten > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30" title="Oubliés (reportés)">
                  <AlertCircle className="w-3 h-3" /> {forgotten}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-slate-500/20 text-slate-400 border border-slate-500/30" title="Restants">
                <Clock className="w-3 h-3" /> {remaining}
              </span>
            </div>
            
            {/* Drop zone for articles */}
            <ArticleDropZone id={`drop-site-${site.id}`} label={site.name} siteId={site.id} isWeekend={false}>
              {todayPubs.length > 0 ? (
                <div className="border-t border-slate-700/50 pt-2">
                  <div className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-900/95 z-10">
                        <tr className="text-slate-500 uppercase">
                          <th className="w-6"></th>
                          <th className="text-left py-1 font-medium">Article</th>
                          <th className="text-left py-1 font-medium w-20">Auteur</th>
                          <th className="text-center py-1 font-medium w-10">Statut</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayPubs.map((pub) => (
                          <DraggableArticleRow key={pub.id} pub={pub} siteId={site.id} onDelete={onDeleteArticle} onStatusChange={onStatusChange} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {todayPubs.length > 5 && (
                    <div className="text-center text-[10px] text-slate-500 mt-1 pt-1 border-t border-slate-700/30">
                      {todayPubs.length} articles au total
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-3 text-slate-500 text-xs border border-dashed border-slate-700 rounded-lg mt-2">
                  Aucun article • Glissez-en ici
                </div>
              )}
            </ArticleDropZone>
            
            {/* Add article button */}
            {showAddArticle ? (
              <div className="mt-3 p-3 bg-slate-800/50 rounded-lg border border-sky-500/30 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={newArticleTitle}
                    onChange={(e) => setNewArticleTitle(e.target.value)}
                    placeholder="Titre de l'article"
                    className="flex-1 h-8 text-sm bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                    disabled={addingArticle}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={newArticleAuthor}
                    onChange={(e) => setNewArticleAuthor(e.target.value)}
                    placeholder="Auteur (optionnel)"
                    className="flex-1 h-8 text-sm bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                    disabled={addingArticle}
                  />
                  {isGroupe && (
                    <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer whitespace-nowrap">
                      <input 
                        type="checkbox" 
                        checked={newArticleIsWeekend} 
                        onChange={(e) => setNewArticleIsWeekend(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900"
                        disabled={addingArticle}
                      />
                      Week-end
                    </label>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button 
                    onClick={() => { setShowAddArticle(false); setNewArticleTitle(''); setNewArticleAuthor(''); }}
                    className="px-3 py-1.5 text-xs text-slate-400 hover:text-white"
                    disabled={addingArticle}
                  >
                    Annuler
                  </button>
                  <button 
                    onClick={handleAddArticle}
                    disabled={addingArticle || !newArticleTitle.trim()}
                    className="px-3 py-1.5 text-xs bg-sky-600 hover:bg-sky-500 text-white rounded disabled:opacity-50 flex items-center gap-1"
                  >
                    {addingArticle ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Ajouter
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={() => setShowAddArticle(true)}
                className="mt-2 w-full py-2 text-xs text-slate-500 hover:text-sky-400 border border-dashed border-slate-700 hover:border-sky-500/50 rounded-lg flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter un article
              </button>
            )}
            
            {/* Weekend section for Groupe sites */}
            {isGroupe && (
              <div className="border-t border-slate-600/50 pt-3 mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-4 h-4 text-sky-400" />
                  <span className="text-xs font-semibold text-sky-400 uppercase tracking-wider">Week-end</span>
                  {weekendPubs.length > 0 && (
                    <span className="text-[10px] text-sky-400/60">({weekendPubs.length})</span>
                  )}
                </div>
                <ArticleDropZone id={`drop-weekend-${site.id}`} label={site.name} siteId={site.id} isWeekend={true}>
                  {weekendPubs.length > 0 ? (
                    <div className="max-h-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/30">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-900/95 z-10">
                          <tr className="text-slate-500 uppercase">
                            <th className="w-6"></th>
                            <th className="text-left py-1 font-medium">Article</th>
                            <th className="text-left py-1 font-medium w-20">Auteur</th>
                            <th className="text-center py-1 font-medium w-10">Statut</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                            {weekendPubs.map((pub) => (
                              <DraggableArticleRow key={pub.id} pub={pub} siteId={site.id} onDelete={onDeleteArticle} onStatusChange={onStatusChange} />
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400 text-[11px] border border-dashed border-slate-600/50 rounded-lg bg-slate-800/30 hover:bg-slate-700/30 transition-colors">
                      Glissez des articles ici pour le week-end
                    </div>
                  )}
                </ArticleDropZone>
              </div>
            )}
            
            {/* Communiqués de presse section for Groupe sites */}
            {isGroupe && cpPubs.length > 0 && (
              <div className="border-t border-violet-500/30 pt-3 mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-violet-400" />
                  <span className="text-xs font-semibold text-violet-400 uppercase tracking-wider">Communiqués</span>
                  <span className="text-[10px] text-violet-400/60">({cpPubs.length})</span>
                  <span className="ml-auto text-[9px] text-violet-400/50 italic">ne compte pas dans l'objectif</span>
                </div>
                <div className="max-h-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-violet-600 scrollbar-track-slate-800/30">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-900/95 z-10">
                      <tr className="text-violet-400/70 uppercase">
                        <th className="text-left py-1 font-medium">Article</th>
                        <th className="text-center py-1 font-medium w-10">Statut</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cpPubs.map((pub) => (
                        <tr key={pub.id} className="border-b border-violet-900/30 hover:bg-violet-950/30 transition-colors">
                          <td className="py-1.5 pr-2">
                            <div className="font-medium text-slate-200 truncate max-w-[180px]" title={pub.title}>
                              {pub.title}
                            </div>
                            <div className="text-[10px] text-violet-400/50">Auto-CP</div>
                          </td>
                          <td className="text-center py-1.5">
                            {pub.publish_status === 'publish' ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400" title="Publié">
                                <CheckCircle2 className="w-3 h-3" />
                              </span>
                            ) : (
                              <button
                                onClick={() => onStatusChange(pub.id, 'publish')}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 hover:bg-emerald-500/30 hover:text-emerald-400 transition-colors"
                                title="Marquer comme publié"
                              >
                                <Clock className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                          <td className="text-right py-1.5">
                            <button
                              onClick={() => onDeleteArticle(pub.id, pub.title)}
                              className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== SITE SECTION ====================
const SiteSection = ({ title, icon: Icon, sites, publications, weekendPublications, cpPublications, siteCategory, isAmiral, onSiteUpdate, onRemoveFromConduite, onDeleteArticle, onAddArticle, onStatusChange, onRefreshRss, onReorder, activeArticleId, allSites, bgClass, textClass }) => {
  const siteIds = useMemo(() => sites.map(s => `site-${s.id}`), [sites]);
  
  if (sites.length === 0) return null;
  
  return (
    <section className="mb-8">
      <div className={`flex items-center justify-between mb-4 bg-slate-800/80 ${bgClass} rounded-lg px-4 py-2`}>
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${textClass}`} />
          <h2 className={`text-xl font-bold text-white uppercase tracking-wide`}>{title}</h2>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-300">
          <span>{sites.length} sites</span>
          <span>Objectif : {sites.reduce((acc, s) => acc + (s.daily_objective || 2), 0)} / jour</span>
        </div>
      </div>
      
      <SortableContext items={siteIds} strategy={horizontalListSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sites.map(site => (
            <SortableSiteCard
              key={site.id}
              site={site}
              publications={publications}
              weekendPublications={weekendPublications}
              cpPublications={cpPublications}
              siteCategory={siteCategory}
              isAmiral={isAmiral}
              onSiteUpdate={onSiteUpdate}
              onRemoveFromConduite={onRemoveFromConduite}
              onDeleteArticle={onDeleteArticle}
              onAddArticle={onAddArticle}
              onStatusChange={onStatusChange}
              onRefreshRss={onRefreshRss}
              activeArticleId={activeArticleId}
              allSites={allSites}
            />
          ))}
        </div>
      </SortableContext>
    </section>
  );
};

// ==================== TRIBUNES SECTION ====================
const TribunesSection = ({ tribunePublications, onStatusChange, onDeleteTribune, onAddTribune, onUpdateUrl, sites }) => {
  const todayTribunes = tribunePublications || [];
  const published = todayTribunes.filter(t => t.publish_status === 'publish').length;
  const drafts = todayTribunes.filter(t => t.publish_status === 'draft').length;
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newTribune, setNewTribune] = useState({ title: '', author_name: '', site_id: '' });
  const [adding, setAdding] = useState(false);
  
  // État pour l'édition d'URL
  const [editingUrlId, setEditingUrlId] = useState(null);
  const [editingUrl, setEditingUrl] = useState('');
  
  const handleAdd = async () => {
    if (!newTribune.title || !newTribune.author_name || !newTribune.site_id) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setAdding(true);
    try {
      await onAddTribune(newTribune);
      setNewTribune({ title: '', author_name: '', site_id: '' });
      setShowAddDialog(false);
      toast.success('Tribune ajoutée');
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAdding(false);
    }
  };
  
  // Gérer la sauvegarde de l'URL
  const handleSaveUrl = async (tribuneId) => {
    if (onUpdateUrl && editingUrl) {
      await onUpdateUrl(tribuneId, editingUrl);
    }
    setEditingUrlId(null);
    setEditingUrl('');
  };
  
  // Ouvrir l'édition d'URL
  const startEditUrl = (tribune) => {
    setEditingUrlId(tribune.id);
    setEditingUrl(tribune.published_url || '');
  };
  
  // Construire l'URL prévisionnelle (brouillon) basée sur le site
  const getPreviewUrl = (tribune) => {
    const site = sites?.find(s => s.id === tribune.site_id);
    if (site?.url) {
      return `${site.url.replace(/\/$/, '')}/wp-admin/edit.php`;
    }
    return null;
  };
  
  // Filtrer uniquement les sites du groupe pour la sélection (seuls les sites Groupe peuvent avoir des tribunes)
  const groupeSites = (sites || []).filter(s => s.site_category === 'groupe');
  
  return (
    <Card className="bg-slate-900/80 border-amber-700/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Feather className="w-5 h-5 text-amber-400" />
            <CardTitle className="text-lg font-bold text-amber-300 uppercase tracking-wide">Tribunes</CardTitle>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-emerald-400">
              <CheckCircle2 className="w-3 h-3" /> Publiées : {published}
            </span>
            <span className="flex items-center gap-1 text-amber-400">
              <Clock className="w-3 h-3" /> Brouillons : {drafts}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
              onClick={() => setShowAddDialog(true)}
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {todayTribunes.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-amber-500/70 uppercase border-b border-amber-700/30">
                <th className="text-left py-2 font-medium">Titre</th>
                <th className="text-left py-2 font-medium w-28">Auteur</th>
                <th className="text-left py-2 font-medium w-28">Site</th>
                <th className="text-center py-2 font-medium w-20">Statut</th>
                <th className="text-center py-2 font-medium w-24">Lien</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {todayTribunes.map((tribune, idx) => (
                <tr key={tribune.id || idx} className="border-t border-amber-700/20 hover:bg-amber-950/20 transition-colors">
                  <td className="py-2 text-slate-200" title={tribune.title}>
                    {tribune.title}
                  </td>
                  <td className="py-2 text-amber-300/80 truncate" title={tribune.author_name}>{tribune.author_name || '-'}</td>
                  <td className="py-2 text-slate-400 truncate" title={tribune.site_name}>{tribune.site_name || '-'}</td>
                  <td className="py-2 text-center">
                    {tribune.publish_status === 'publish' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        Publié
                      </span>
                    ) : (
                      <button
                        onClick={() => onStatusChange && onStatusChange(tribune.id, 'publish')}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 hover:bg-emerald-500/30 hover:text-emerald-400 text-[10px] font-medium transition-colors"
                        title="Marquer comme publié"
                      >
                        <Clock className="w-3 h-3" />
                        Brouillon
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-center">
                    {editingUrlId === tribune.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingUrl}
                          onChange={(e) => setEditingUrl(e.target.value)}
                          placeholder="https://..."
                          className="w-24 px-1.5 py-0.5 text-[10px] bg-slate-800 border border-amber-500/50 rounded text-white"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveUrl(tribune.id)}
                          className="p-0.5 text-emerald-400 hover:text-emerald-300"
                          title="Enregistrer"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => { setEditingUrlId(null); setEditingUrl(''); }}
                          className="p-0.5 text-slate-500 hover:text-slate-300"
                          title="Annuler"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : tribune.published_url ? (
                      <div className="flex items-center justify-center gap-1">
                        <a
                          href={tribune.published_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 text-[10px] font-medium"
                          title={tribune.published_url}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Voir
                        </a>
                        <button
                          onClick={() => startEditUrl(tribune)}
                          className="p-0.5 text-slate-500 hover:text-amber-400"
                          title="Modifier le lien"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : tribune.publish_status === 'draft' ? (
                      <div className="flex items-center justify-center gap-1">
                        {getPreviewUrl(tribune) && (
                          <a
                            href={getPreviewUrl(tribune)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 text-[10px]"
                            title="Ouvrir le back-office WordPress"
                          >
                            <Link className="w-3 h-3" />
                            WP
                          </a>
                        )}
                        <button
                          onClick={() => startEditUrl(tribune)}
                          className="p-0.5 text-amber-500/70 hover:text-amber-400"
                          title="Ajouter le lien de la tribune"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditUrl(tribune)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-[10px]"
                        title="Ajouter le lien publié"
                      >
                        <Plus className="w-3 h-3" />
                        Lien
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => onDeleteTribune && onDeleteTribune(tribune.id, tribune.title)}
                      className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                      title="Supprimer la tribune"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-6 text-slate-400 text-sm">
            <Feather className="w-8 h-8 mx-auto mb-2 text-amber-500/30" />
            Aucune tribune aujourd'hui
          </div>
        )}
        
        {/* Dialog d'ajout de tribune */}
        <AlertDialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <AlertDialogContent className="bg-slate-900 border-amber-700/50">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-amber-300 flex items-center gap-2">
                <Feather className="w-5 h-5" />
                Ajouter une tribune
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Ajoutez manuellement une tribune à la conduite éditoriale.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-xs text-amber-400/80 mb-1">Titre de la tribune *</label>
                <Input
                  value={newTribune.title}
                  onChange={(e) => setNewTribune(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: L'avenir de l'économie française"
                  className="bg-slate-800 border-amber-700/30 text-white"
                />
              </div>
              
              <div>
                <label className="block text-xs text-amber-400/80 mb-1">Auteur *</label>
                <Input
                  value={newTribune.author_name}
                  onChange={(e) => setNewTribune(prev => ({ ...prev, author_name: e.target.value }))}
                  placeholder="Ex: Jean-Michel Expert"
                  className="bg-slate-800 border-amber-700/30 text-white"
                />
              </div>
              
              <div>
                <label className="block text-xs text-amber-400/80 mb-1">Site de destination *</label>
                <select
                  value={newTribune.site_id}
                  onChange={(e) => setNewTribune(prev => ({ ...prev, site_id: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md bg-slate-800 border border-amber-700/30 text-white text-sm"
                >
                  <option value="">Sélectionner un site...</option>
                  {groupeSites.map(site => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </AlertDialogCancel>
              <Button
                onClick={handleAdd}
                disabled={adding || !newTribune.title || !newTribune.author_name || !newTribune.site_id}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Ajouter
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

// ==================== NOTES SECTION ====================
const NotesSection = ({ notes, onSave, isAmiral, isEditing, setIsEditing, editValue, setEditValue, saving }) => {
  const handleSave = async () => {
    await onSave(editValue);
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setEditValue(notes);
    setIsEditing(false);
  };
  
  return (
    <div className="flex items-start gap-3 text-sm">
      <Clipboard className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-sky-400 uppercase">Notes du jour</span>
          {isAmiral && !isEditing && (
            <button 
              onClick={() => setIsEditing(true)}
              className="p-0.5 text-slate-500 hover:text-sky-400 transition-colors"
              title="Modifier les notes"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="flex items-start gap-2">
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 bg-slate-800 border border-sky-500/50 rounded px-3 py-2 text-sm text-slate-200 resize-none focus:outline-none focus:border-sky-400"
              rows={2}
              placeholder="Notes pour l'équipe..."
              autoFocus
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded transition-colors"
                title="Enregistrer"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button
                onClick={handleCancel}
                className="p-1.5 bg-slate-700 text-slate-400 hover:bg-slate-600 rounded transition-colors"
                title="Annuler"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-300 whitespace-pre-wrap">
            {notes || <span className="italic text-slate-600">Aucune note pour aujourd'hui</span>}
          </p>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const ConduitePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [publications, setPublications] = useState([]);
  const [weekendPublications, setWeekendPublications] = useState([]);
  const [cpPublications, setCpPublications] = useState([]); // Communiqués de presse
  const [tribunePublications, setTribunePublications] = useState([]); // Tribunes/Éditos
  const [tribunes, setTribunes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activeId, setActiveId] = useState(null);
  const [activeArticleId, setActiveArticleId] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [showAddSiteDialog, setShowAddSiteDialog] = useState(false);
  const [addingSite, setAddingSite] = useState(false);
  const [rssChecking, setRssChecking] = useState(false);
  
  // États pour la configuration des alertes horaires (amiraux)
  const [showAlertConfigDialog, setShowAlertConfigDialog] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertConfigLoading, setAlertConfigLoading] = useState(false);
  const [savingAlertConfig, setSavingAlertConfig] = useState(false);
  
  // États pour la purge de la conduite (amiraux)
  const [showPurgeDialog1, setShowPurgeDialog1] = useState(false);
  const [showPurgeDialog2, setShowPurgeDialog2] = useState(false);
  const [purging, setPurging] = useState(false);
  
  // État pour les utilisateurs (pour le panel des absences)
  const [allUsers, setAllUsers] = useState([]);
  
  // État pour la vérification Brave Search
  const [showBraveSearchDialog, setShowBraveSearchDialog] = useState(false);
  const [braveSearching, setBraveSearching] = useState(false);
  const [braveSearchResult, setBraveSearchResult] = useState(null);
  
  // État pour les notes du jour
  const [conduiteNotes, setConduiteNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotesValue, setEditNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  const isAmiral = user?.grade === 'amiral';
  const isCapitaine = user?.grade === 'capitaine' || isAmiral;
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const handleSiteUpdate = useCallback((siteId, updates) => {
    setSites(prev => prev.map(s => s.id === siteId ? { ...s, ...updates } : s));
  }, []);
  
  // Load available sites for adding to conduite
  const loadAvailableSites = useCallback(async () => {
    if (!isAmiral) return;
    try {
      const res = await axios.get(`${API}/api/conduite/available-sites`);
      setAvailableSites(res.data || []);
    } catch (error) {
      console.error('Error loading available sites:', error);
    }
  }, [isAmiral]);
  
  // Add site to conduite
  const handleAddSiteToConduite = async (siteId, siteName) => {
    setAddingSite(true);
    try {
      await axios.patch(`${API}/api/conduite/site/${siteId}/toggle?action=add`);
      toast.success(`Site "${siteName}" ajouté à la conduite`);
      setShowAddSiteDialog(false);
      // Immediate local state update
      setAvailableSites(prev => prev.filter(s => s.id !== siteId));
      // Reload data from server
      const sitesRes = await axios.get(`${API}/api/wordpress-sites`);
      const allSites = sitesRes.data || [];
      setSites(allSites.filter(s => s.in_conduite === true));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setAddingSite(false);
    }
  };
  
  // Remove site from conduite
  const handleRemoveFromConduite = async (siteId, siteName) => {
    try {
      await axios.patch(`${API}/api/conduite/site/${siteId}/toggle?action=remove`);
      toast.success(`Site "${siteName}" retiré de la conduite`);
      // Immediate local state update - remove from displayed sites
      setSites(prev => prev.filter(s => s.id !== siteId));
      // Update available sites
      const availableRes = await axios.get(`${API}/api/conduite/available-sites`);
      setAvailableSites(availableRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  // Vérification RSS manuelle (Amiraux uniquement)
  const handleRssCheck = async () => {
    if (!isAmiral) return;
    setRssChecking(true);
    try {
      const res = await axios.post(`${API}/api/rss-check`);
      const stats = res.data?.stats;
      if (stats) {
        const { sites_checked, publications_checked, publications_confirmed } = stats;
        if (publications_confirmed > 0) {
          toast.success(
            `✅ Vérification RSS terminée : ${publications_confirmed} article(s) confirmé(s) sur ${publications_checked} vérifié(s)`,
            { duration: 5000 }
          );
          // Recharger les données pour afficher les mises à jour
          loadData();
        } else if (publications_checked > 0) {
          toast.info(`📡 ${sites_checked} site(s) vérifié(s), ${publications_checked} article(s) en attente - aucune correspondance trouvée`);
        } else {
          toast.info(`📡 ${sites_checked} site(s) vérifié(s) - aucun article en brouillon à vérifier`);
        }
      } else {
        toast.success('Vérification RSS terminée');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification RSS');
    } finally {
      setRssChecking(false);
    }
  };
  
  // Vérification Brave Search (manuelle, réservée aux amiraux)
  const handleBraveSearch = async () => {
    if (!isAmiral) return;
    setBraveSearching(true);
    setBraveSearchResult(null);
    try {
      const res = await axios.post(`${API}/api/brave-search/verify-all-pending`);
      const stats = res.data?.stats;
      setBraveSearchResult(stats);
      
      if (stats) {
        if (stats.publications_confirmed > 0) {
          toast.success(
            `🔍 Brave Search : ${stats.publications_confirmed} article(s) validé(s) sur ${stats.publications_checked} vérifié(s)`,
            { duration: 5000 }
          );
          // Recharger les données
          loadData();
        } else if (stats.publications_checked > 0) {
          toast.info(`🔍 ${stats.publications_checked} article(s) vérifié(s) - aucun trouvé sur les sites`);
        } else {
          toast.info('🔍 Aucun article en brouillon à vérifier');
        }
        
        // Afficher une alerte si on approche de la limite
        if (stats.credits_remaining && stats.credits_remaining < 100) {
          toast.warning(`⚠️ Attention : seulement ${stats.credits_remaining} crédits Brave Search restants ce mois-ci`);
        }
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Limite de crédits Brave Search atteinte pour ce mois');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la vérification Brave Search');
      }
    } finally {
      setBraveSearching(false);
      setShowBraveSearchDialog(false);
    }
  };
  
  // Configuration des alertes horaires (amiraux)
  const loadAlertConfig = async () => {
    setAlertConfigLoading(true);
    try {
      const res = await axios.get(`${API}/api/conduite/alerts/config`);
      setAlertConfig(res.data.config);
    } catch (error) {
      console.error('Error loading alert config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setAlertConfigLoading(false);
    }
  };

  const saveAlertConfig = async (newConfig) => {
    setSavingAlertConfig(true);
    try {
      const res = await axios.put(`${API}/api/conduite/alerts/config`, newConfig);
      setAlertConfig(res.data.config);
      toast.success('Configuration des alertes sauvegardée');
      setShowAlertConfigDialog(false);
    } catch (error) {
      console.error('Error saving alert config:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingAlertConfig(false);
    }
  };

  const openAlertConfigDialog = () => {
    loadAlertConfig();
    setShowAlertConfigDialog(true);
  };
  
  // Purge de la conduite (amiraux)
  const handlePurgeConduite = async () => {
    setPurging(true);
    try {
      const res = await axios.delete(`${API}/api/publications/all?confirm_code=PURGE-CONDUITE`);
      toast.success(`Conduite purgée : ${res.data.deleted_count} articles supprimés`);
      setShowPurgeDialog2(false);
      // Recharger les données
      loadData();
    } catch (error) {
      console.error('Error purging conduite:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurging(false);
    }
  };
  
  // Delete article from conduite
  const handleDeleteArticle = async (publicationId, title) => {
    try {
      await axios.delete(`${API}/api/publications/${publicationId}`);
      toast.success(`Article "${title?.slice(0, 30)}..." supprimé`);
      // Remove from local state immediately
      setPublications(prev => prev.filter(p => p.id !== publicationId));
      setWeekendPublications(prev => prev.filter(p => p.id !== publicationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  // Add manual article to conduite
  const handleAddArticle = async (title, siteId, authorName, isWeekend) => {
    try {
      const params = new URLSearchParams({
        title,
        site_id: siteId,
        is_weekend: isWeekend
      });
      if (authorName) params.append('author_name', authorName);
      
      const res = await axios.post(`${API}/api/publications/manual?${params.toString()}`);
      toast.success(`Article "${title.slice(0, 30)}..." ajouté`);
      
      // Add to local state immediately
      const newPub = res.data?.publication;
      if (newPub) {
        if (isWeekend) {
          setWeekendPublications(prev => [...prev, newPub]);
        } else {
          setPublications(prev => [...prev, newPub]);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
      throw error; // Re-throw to let the component handle loading state
    }
  };
  
  // Change article status (toggle between publish and draft)
  const handleStatusChange = async (publicationId, newStatus, title) => {
    try {
      await axios.patch(`${API}/api/publications/${publicationId}/status?status=${newStatus}`);
      const statusLabel = newStatus === 'publish' ? 'publié' : newStatus === 'draft' ? 'brouillon' : newStatus;
      toast.success(`Article "${title?.slice(0, 30)}..." → ${statusLabel}`);
      
      // Update local state immediately for publications
      setPublications(prev => prev.map(p => 
        p.id === publicationId ? { ...p, publish_status: newStatus } : p
      ));
      setWeekendPublications(prev => prev.map(p => 
        p.id === publicationId ? { ...p, publish_status: newStatus } : p
      ));
      setCpPublications(prev => prev.map(p => 
        p.id === publicationId ? { ...p, publish_status: newStatus } : p
      ));
      
      // Recharger les sites pour mettre à jour les compteurs d'objectifs
      const conduiteSitesRes = await axios.get(`${API}/api/conduite/sites`);
      const conduiteData = conduiteSitesRes.data || {};
      const conduiteSites = [
        ...(conduiteData.groupe_sites || []),
        ...(conduiteData.va_sites || []),
        ...(conduiteData.autre_sites || [])
      ];
      setSites(conduiteSites);
      
      // Mettre à jour aussi les tribunes
      setTribunePublications(prev => prev.map(p => 
        p.id === publicationId ? { ...p, publish_status: newStatus } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
      throw error;
    }
  };
  
  // Ajouter une tribune manuellement
  const handleAddTribune = async (tribuneData) => {
    try {
      const site = sites.find(s => s.id === tribuneData.site_id);
      const params = new URLSearchParams({
        title: tribuneData.title,
        site_id: tribuneData.site_id,
        author_name: tribuneData.author_name,
        is_tribune: 'true'
      });
      
      const res = await axios.post(`${API}/api/publications/manual?${params.toString()}`);
      
      // Add to local state immediately
      const newPub = res.data?.publication;
      if (newPub) {
        setTribunePublications(prev => [...prev, newPub]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de la tribune');
      throw error;
    }
  };
  
  // Supprimer une tribune
  const handleDeleteTribune = async (publicationId, title) => {
    try {
      await axios.delete(`${API}/api/publications/${publicationId}`);
      toast.success(`Tribune "${title?.slice(0, 30)}..." supprimée`);
      // Remove from local state immediately
      setTribunePublications(prev => prev.filter(p => p.id !== publicationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  // Mettre à jour l'URL d'une tribune
  const handleUpdateTribuneUrl = async (publicationId, newUrl) => {
    try {
      await axios.patch(`${API}/api/publications/${publicationId}/url?published_url=${encodeURIComponent(newUrl)}`);
      toast.success('Lien de la tribune mis à jour');
      // Update local state
      setTribunePublications(prev => prev.map(p => 
        p.id === publicationId ? { ...p, published_url: newUrl } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du lien');
    }
  };
  
  // Rafraîchir le flux RSS pour un site spécifique
  const handleRefreshRss = async (siteId) => {
    try {
      const res = await axios.post(`${API}/api/publications/rss-check/${siteId}`);
      const result = res.data;
      
      // Si des articles ont été confirmés, recharger les publications
      if (result?.publications_confirmed > 0) {
        // Recharger les données pour mettre à jour l'affichage
        const today = new Date().toISOString().split('T')[0];
        const pubsRes = await axios.get(`${API}/api/publications?date=${today}`);
        const allTodayPubs = pubsRes.data?.publications || pubsRes.data || [];
        
        const regularPubs = allTodayPubs.filter(p => !p.is_weekend && !p.is_cp && !p.is_tribune);
        const cpPubs = allTodayPubs.filter(p => !p.is_weekend && p.is_cp);
        const tribunePubs = allTodayPubs.filter(p => !p.is_weekend && p.is_tribune);
        
        setPublications(regularPubs);
        setCpPublications(cpPubs);
        setTribunePublications(tribunePubs);
      }
      
      return result;
    } catch (error) {
      console.error('Erreur RSS check:', error);
      throw error;
    }
  };
  
  // Sauvegarde des notes du jour
  const handleSaveNotes = async (content) => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/api/conduite/notes`, { content });
      setConduiteNotes(content);
      toast.success('Notes enregistrées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingNotes(false);
    }
  };
  
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Récupérer les sites de la conduite avec leurs statistiques d'objectifs
      const conduiteSitesRes = await axios.get(`${API}/api/conduite/sites`);
      const conduiteData = conduiteSitesRes.data || {};
      const conduiteSites = [
        ...(conduiteData.groupe_sites || []),
        ...(conduiteData.va_sites || []),
        ...(conduiteData.autre_sites || [])
      ];
      setSites(conduiteSites);
      
      const today = new Date().toISOString().split('T')[0];
      const pubsRes = await axios.get(`${API}/api/publications?date=${today}`);
      const allTodayPubs = pubsRes.data?.publications || pubsRes.data || [];
      
      // Séparer les communiqués de presse et tribunes des autres publications
      const regularPubs = allTodayPubs.filter(p => !p.is_weekend && !p.is_cp && !p.is_tribune);
      const cpPubs = allTodayPubs.filter(p => !p.is_weekend && p.is_cp);
      const tribunePubs = allTodayPubs.filter(p => !p.is_weekend && p.is_tribune);
      
      setPublications(regularPubs);
      setCpPublications(cpPubs);
      setTribunePublications(tribunePubs);
      
      try {
        const now = new Date();
        const dayOfWeek = now.getDay(); // 0=dimanche, 6=samedi
        
        // Calculer le samedi et dimanche du week-end en cours ou à venir
        let saturday, sunday;
        
        if (dayOfWeek === 0) {
          // Dimanche : le week-end en cours est hier (samedi) et aujourd'hui (dimanche)
          saturday = new Date(now);
          saturday.setDate(now.getDate() - 1);
          sunday = new Date(now);
        } else if (dayOfWeek === 6) {
          // Samedi : le week-end en cours est aujourd'hui et demain
          saturday = new Date(now);
          sunday = new Date(now);
          sunday.setDate(now.getDate() + 1);
        } else {
          // Lundi à vendredi : prochain week-end
          const daysUntilSaturday = 6 - dayOfWeek;
          saturday = new Date(now);
          saturday.setDate(now.getDate() + daysUntilSaturday);
          sunday = new Date(saturday);
          sunday.setDate(saturday.getDate() + 1);
        }
        
        const [satPubs, sunPubs, weekendFlagged] = await Promise.all([
          axios.get(`${API}/api/publications?date=${saturday.toISOString().split('T')[0]}`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/publications?date=${sunday.toISOString().split('T')[0]}`).catch(() => ({ data: [] })),
          // Also get all publications with is_weekend flag from today (they were moved)
          Promise.resolve({ data: allTodayPubs.filter(p => p.is_weekend) })
        ]);
        const satData = satPubs.data?.publications || satPubs.data || [];
        const sunData = sunPubs.data?.publications || sunPubs.data || [];
        const flaggedData = weekendFlagged.data || [];
        
        // Combine and dedupe by id
        const allWeekend = [...satData, ...sunData, ...flaggedData];
        const uniqueWeekend = allWeekend.filter((pub, idx, arr) => 
          arr.findIndex(p => p.id === pub.id) === idx
        );
        setWeekendPublications(uniqueWeekend);
      } catch { setWeekendPublications([]); }
      
      try {
        const tribunesRes = await axios.get(`${API}/api/tribunes?status=all&limit=10`);
        setTribunes(tribunesRes.data?.tribunes || []);
      } catch { setTribunes([]); }
      
      setLastRefresh(new Date());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
    loadAvailableSites();
    
    // Charger la liste des utilisateurs pour le panel absences (amiraux uniquement)
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API}/api/users`);
        setAllUsers(res.data || []);
      } catch (error) {
        // Non bloquant si l'utilisateur n'a pas accès
        console.debug('Users load skipped (non-admin)');
      }
    };
    loadUsers();
    
    // Charger les notes du jour
    const loadNotes = async () => {
      try {
        const res = await axios.get(`${API}/api/conduite/notes`);
        const notesContent = res.data?.notes || '';
        setConduiteNotes(notesContent);
        setEditNotesValue(notesContent);
      } catch (error) {
        console.debug('Notes load failed:', error);
      }
    };
    loadNotes();
    
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData, loadAvailableSites]);
  
  // Separate sites by category
  const groupeSites = useMemo(() => sites.filter(s => s.site_category === 'groupe').sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)), [sites]);
  const vaSites = useMemo(() => sites.filter(s => s.site_category === 'va' || (s.is_va && s.site_category !== 'groupe')).sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)), [sites]);
  const autreSites = useMemo(() => sites.filter(s => !s.site_category || s.site_category === 'autre' || (!s.is_va && s.site_category !== 'groupe' && s.site_category !== 'va')).sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)), [sites]);
  
  const totalPublished = publications.filter(p => p.publish_status === 'publish').length;
  const totalInProgress = publications.filter(p => p.publish_status === 'draft' || p.publish_status === 'pending').length;
  const totalForgotten = publications.filter(p => p.publish_status === 'forgotten').length;
  const totalObjective = sites.reduce((acc, s) => acc + (s.daily_objective || 2), 0);
  const totalRemaining = Math.max(0, totalObjective - totalPublished - totalInProgress - totalForgotten);
  
  const todayFormatted = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  
  // Custom collision detection - prioritize weekend zones over site zones
  const customCollisionDetection = useCallback((args) => {
    const { droppableContainers, pointerCoordinates, active } = args;
    
    if (!pointerCoordinates) {
      return closestCenter(args);
    }
    
    const activeIdStr = active?.id?.toString() || '';
    const isArticleDrag = activeIdStr.startsWith('article-');
    const isSiteDrag = activeIdStr.startsWith('site-');
    
    // For SITE dragging - only consider other site containers
    if (isSiteDrag) {
      const siteContainers = [];
      droppableContainers.forEach((container) => {
        const rect = container.rect.current;
        if (!rect) return;
        
        const id = container.id.toString();
        // Only consider site-xxx containers, not drop-site-xxx
        if (!id.startsWith('site-')) return;
        
        const isInside = 
          pointerCoordinates.x >= rect.left &&
          pointerCoordinates.x <= rect.right &&
          pointerCoordinates.y >= rect.top &&
          pointerCoordinates.y <= rect.bottom;
        
        if (isInside) {
          const distance = Math.hypot(
            pointerCoordinates.x - (rect.left + rect.width / 2),
            pointerCoordinates.y - (rect.top + rect.height / 2)
          );
          siteContainers.push({ id: container.id, data: container.data, distance });
        }
      });
      
      if (siteContainers.length > 0) {
        // Return the closest site container
        siteContainers.sort((a, b) => a.distance - b.distance);
        console.log('[DnD] Site drag - selected:', siteContainers[0].id);
        return [{ id: siteContainers[0].id, data: siteContainers[0].data }];
      }
      return [];
    }
    
    // For ARTICLE dragging - find drop zones
    if (isArticleDrag) {
      // Find all drop zones the pointer is inside
      const matchingDropZones = [];
      
      droppableContainers.forEach((container) => {
        const rect = container.rect.current;
        if (!rect) return;
        
        const id = container.id.toString();
        if (!id.startsWith('drop-')) return;
        
        const isInside = 
          pointerCoordinates.x >= rect.left &&
          pointerCoordinates.x <= rect.right &&
          pointerCoordinates.y >= rect.top &&
          pointerCoordinates.y <= rect.bottom;
        
        if (isInside) {
          const area = (rect.right - rect.left) * (rect.bottom - rect.top);
          matchingDropZones.push({
            id: container.id,
            data: container.data,
            isWeekend: id.includes('weekend'),
            area,
            rect
          });
        }
      });
      
      if (matchingDropZones.length > 0) {
        // Sort by: 1) weekend zones first, 2) smaller area (more specific)
        matchingDropZones.sort((a, b) => {
          // Weekend zones always win
          if (a.isWeekend && !b.isWeekend) return -1;
          if (!a.isWeekend && b.isWeekend) return 1;
          // Then prefer smaller (more specific) zones
          return a.area - b.area;
        });
        
        const selected = matchingDropZones[0];
        console.log('[DnD] Article drag - selected:', selected.id, 'isWeekend:', selected.isWeekend);
        return [{ id: selected.id, data: selected.data }];
      }
    }
    
    return [];
  }, []);
  
  // DnD handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    if (active.id.toString().startsWith('article-')) {
      setActiveArticleId(active.id);
    }
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveArticleId(null);
    
    if (!over) {
      console.log('No drop target');
      return;
    }
    
    const activeIdStr = active.id.toString();
    const overIdStr = over.id.toString();
    
    console.log('Drag end:', { activeIdStr, overIdStr, overData: over.data?.current });
    
    // Article moving to drop zone
    if (activeIdStr.startsWith('article-') && overIdStr.startsWith('drop-')) {
      if (!isCapitaine) {
        toast.error('Permission insuffisante');
        return;
      }
      
      const articleId = activeIdStr.replace('article-', '');
      const dropData = over.data?.current;
      const isWeekend = dropData?.isWeekend || overIdStr.includes('weekend');
      const targetSiteId = dropData?.siteId || overIdStr.replace('drop-site-', '').replace('drop-weekend-', '');
      
      // Récupérer les infos de l'article source
      const sourceData = active.data?.current;
      const sourceIsWeekend = sourceData?.isWeekend || false;
      
      // Vérifier si c'est un déplacement significatif:
      // - Même site ET même section (jour->jour ou WE->WE) = pas de déplacement nécessaire
      // - Même site MAIS section différente (jour->WE ou WE->jour) = déplacement nécessaire
      // - Site différent = toujours déplacement nécessaire
      const sameSite = sourceData?.sourceSiteId === targetSiteId;
      const sameSection = sourceIsWeekend === isWeekend;
      
      if (sameSite && sameSection) {
        console.log('Same site and same section, no move needed');
        return;
      }
      
      console.log('Moving article:', { articleId, targetSiteId, isWeekend, fromWeekend: sourceIsWeekend });
      
      try {
        await axios.patch(`${API}/api/conduite/move-article?publication_id=${articleId}&target_site_id=${targetSiteId}&is_weekend=${isWeekend}`);
        toast.success(isWeekend ? 'Article déplacé vers le week-end' : 'Article déplacé');
        loadData();
      } catch (error) {
        console.error('Move error:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors du déplacement');
      }
      return;
    }
    
    // Site reordering
    if (activeIdStr.startsWith('site-') && overIdStr.startsWith('site-')) {
      const activeSiteId = activeIdStr.replace('site-', '');
      const overSiteId = overIdStr.replace('site-', '');
      
      if (activeSiteId === overSiteId) return;
      
      // Find which category both sites belong to
      let category = null;
      let siteList = null;
      
      if (groupeSites.find(s => s.id === activeSiteId) && groupeSites.find(s => s.id === overSiteId)) {
        category = 'groupe';
        siteList = groupeSites;
      } else if (vaSites.find(s => s.id === activeSiteId) && vaSites.find(s => s.id === overSiteId)) {
        category = 'va';
        siteList = vaSites;
      } else if (autreSites.find(s => s.id === activeSiteId) && autreSites.find(s => s.id === overSiteId)) {
        category = 'autre';
        siteList = autreSites;
      }
      
      if (category && siteList && isAmiral) {
        const oldIndex = siteList.findIndex(s => s.id === activeSiteId);
        const newIndex = siteList.findIndex(s => s.id === overSiteId);
        
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          console.log('[DnD] Skip - same position or invalid index');
          return;
        }
        
        console.log('[DnD] ===== DRAG START =====');
        console.log('[DnD] Active:', siteList[oldIndex]?.name, '(oldIndex:', oldIndex, ')');
        console.log('[DnD] Over:', siteList[newIndex]?.name, '(newIndex:', newIndex, ')');
        console.log('[DnD] Avant:', siteList.map(s => s.name).join(' → '));
        
        // Utiliser arrayMove de @dnd-kit/sortable pour le bon comportement
        // arrayMove déplace l'élément de oldIndex vers newIndex
        // ex: [A,B,C,D], move(3,1) → [A,D,B,C] (D prend la place de B, B et C décalent)
        const newOrder = arrayMove(siteList, oldIndex, newIndex);
        
        console.log('[DnD] Après:', newOrder.map(s => s.name).join(' → '));
        console.log('[DnD] ===== DRAG END =====');
        
        // Update local state immediately with new display_order
        setSites(prev => {
          // Get sites not in this category
          const otherSites = prev.filter(s => !siteList.find(ls => ls.id === s.id));
          // Update display_order for reordered sites
          const updatedSites = newOrder.map((s, idx) => ({ ...s, display_order: idx }));
          return [...otherSites, ...updatedSites];
        });
        
        // Save to backend
        try {
          const reorderUrl = `${API}/api/conduite/reorder-sites?category=${category}&${newOrder.map(s => `site_ids=${s.id}`).join('&')}`;
          await axios.patch(reorderUrl);
          toast.success('Ordre des sites mis à jour');
        } catch (error) {
          console.error('[DnD] Erreur:', error);
          toast.error('Erreur lors de la sauvegarde');
          loadData(); // Reload on error
        }
      }
    }
  };
  
  if (loading && sites.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-sky-400 mx-auto mb-4" />
          <p className="text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }
  
  return (
    <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="min-h-screen p-4 lg:p-6" data-testid="conduite-page">
        {/* Header */}
        <div className="mb-6 bg-slate-900/80 rounded-xl p-4 border border-slate-700/50">
          {/* Ligne 1 : Titre et date */}
          <div className="flex items-center gap-4 mb-3">
            <h1 className="text-2xl lg:text-3xl font-bold font-orbitron text-sky-400 uppercase tracking-wider">
              Conduite Éditoriale
            </h1>
            <span className="text-slate-400 text-sm capitalize">{todayFormatted}</span>
            <span className="text-slate-600 text-xs">• Mis à jour : {lastRefresh.toLocaleTimeString('fr-FR')}</span>
          </div>
          
          {/* Ligne 2 : Compteurs + Boutons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Compteurs */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30" title="Articles publiés">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-base font-bold text-emerald-400">{totalPublished}</span>
              <span className="text-[10px] text-emerald-400/80">publiés</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30" title="Rédigés (pas en ligne)">
              <Loader2 className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-base font-bold text-amber-400">{totalInProgress}</span>
              <span className="text-[10px] text-amber-400/80">en cours</span>
            </div>
            {totalForgotten > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30" title="Oubliés (reportés du jour précédent)">
                <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-base font-bold text-red-400">{totalForgotten}</span>
                <span className="text-[10px] text-red-400/80">oubliés</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-500/10 border border-slate-500/30" title="Restants à produire">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-base font-bold text-slate-300">{totalRemaining}</span>
              <span className="text-[10px] text-slate-400">restants</span>
            </div>
            
            {/* Séparateur */}
            <div className="w-px h-6 bg-slate-700 mx-1"></div>
            
            {/* Boutons d'action */}
            <Button onClick={loadData} disabled={loading} variant="outline" size="sm" className="h-8 px-2 border-slate-600 text-slate-400 hover:text-sky-400">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {isAmiral && (
              <Button 
                onClick={handleRssCheck} 
                disabled={rssChecking} 
                variant="outline" 
                size="sm" 
                className="h-8 border-orange-600/50 text-orange-400 hover:bg-orange-600/20 hover:text-orange-300"
                title="Vérifier les publications via les flux RSS des sites"
              >
                <Rss className={`w-3.5 h-3.5 mr-1 ${rssChecking ? 'animate-pulse' : ''}`} />
                <span className="text-xs">Vérifier RSS</span>
              </Button>
            )}
            
            {isAmiral && (
              <Button 
                onClick={() => setShowBraveSearchDialog(true)} 
                disabled={braveSearching} 
                variant="outline" 
                size="sm" 
                className="h-8 border-sky-600/50 text-sky-400 hover:bg-sky-600/20 hover:text-sky-300"
                title="Rechercher les articles non validés via Brave Search (consomme des crédits)"
                data-testid="conduite-brave-search-btn"
              >
                <Search className={`w-3.5 h-3.5 mr-1 ${braveSearching ? 'animate-spin' : ''}`} />
                <span className="text-xs">Brave Search</span>
              </Button>
            )}
            
            {isAmiral && (
              <Button 
                onClick={openAlertConfigDialog}
                variant="outline" 
                size="sm" 
                className="h-8 border-amber-600/50 text-amber-400 hover:bg-amber-600/20 hover:text-amber-300"
                title="Configurer les alertes horaires de production"
                data-testid="conduite-alert-config-btn"
              >
                <Bell className="w-3.5 h-3.5 mr-1" />
                <span className="text-xs">Alertes</span>
              </Button>
            )}
            
            {isAmiral && (
              <Button 
                onClick={() => setShowPurgeDialog1(true)}
                variant="outline" 
                size="sm" 
                className="h-8 border-red-600/50 text-red-400 hover:bg-red-600/20 hover:text-red-300"
                title="Supprimer tous les articles de la conduite"
                data-testid="conduite-purge-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                <span className="text-xs">Purger</span>
              </Button>
            )}
            
            {/* Bouton Historique - visible pour tous */}
            <Button 
              onClick={() => navigate('/conduite/historique')}
              variant="outline" 
              size="sm" 
              className="h-8 border-violet-600/50 text-violet-400 hover:bg-violet-600/20 hover:text-violet-300"
              title="Voir l'historique des conduites"
              data-testid="conduite-history-btn"
            >
              <History className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Historique</span>
            </Button>
          </div>
          
          {/* Ligne 3 : Barre de progression */}
          <div className="mt-3">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs text-slate-500">Progression</span>
              <span className="text-xs font-mono text-slate-400">
                {totalPublished + totalInProgress} / {totalObjective}
              </span>
              <span className="text-xs font-bold text-sky-400">
                {totalObjective > 0 ? Math.round((totalPublished / totalObjective) * 100) : 0}% publiés
              </span>
            </div>
            <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden flex">
              {totalPublished > 0 && (
                <div 
                  className="h-full bg-emerald-500 transition-all duration-500" 
                  style={{ width: `${(totalPublished / totalObjective) * 100}%` }}
                  title={`${totalPublished} publiés`}
                />
              )}
              {totalInProgress > 0 && (
                <div 
                  className="h-full bg-amber-500 transition-all duration-500" 
                  style={{ width: `${(totalInProgress / totalObjective) * 100}%` }}
                  title={`${totalInProgress} en cours`}
                />
              )}
              {totalRemaining > 0 && (
                <div 
                  className="h-full bg-red-500/70 transition-all duration-500" 
                  style={{ width: `${(totalRemaining / totalObjective) * 100}%` }}
                  title={`${totalRemaining} restants`}
                />
              )}
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  {totalPublished} publiés
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  {totalInProgress} en cours
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500/70"></span>
                  {totalRemaining} restants
                </span>
              </div>
              <span className="text-slate-600">Objectif: {totalObjective}</span>
            </div>
          </div>
          
          {/* Bouton Ajouter un site - Amiraux uniquement */}
          {isAmiral && availableSites.length > 0 && (
            <Dialog open={showAddSiteDialog} onOpenChange={setShowAddSiteDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="mt-3 border-sky-600 text-sky-400 hover:bg-sky-600/20">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter un site
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Ajouter un site à la conduite</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Sélectionnez un site à ajouter au tableau de bord de la conduite éditoriale.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {availableSites.map(site => (
                    <AlertDialog key={site.id}>
                      <AlertDialogTrigger asChild>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 cursor-pointer border border-slate-700 hover:border-sky-500/50 transition-all">
                          <div className="flex items-center gap-3">
                            {site.url && (
                              <img 
                                src={getFaviconUrl(site.url)} 
                                alt="" 
                                className="w-6 h-6 rounded bg-white/20 p-0.5"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-white">{site.name}</div>
                              {site.is_va && <span className="text-xs text-slate-400">VA</span>}
                            </div>
                          </div>
                          <Plus className="w-4 h-4 text-slate-400" />
                        </div>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Ajouter ce site à la conduite ?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Vous êtes sur le point d'ajouter <span className="font-semibold text-sky-400">{site.name}</span> à la conduite éditoriale.
                            Le site apparaîtra dans le tableau de bord.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">Annuler</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleAddSiteToConduite(site.id, site.name)}
                            disabled={addingSite}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            {addingSite ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                            Ajouter à la conduite
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          <div className="mt-4 pt-4 border-t border-slate-700/50 text-sm">
            <AbsencesPanel currentUser={user} users={allUsers} compact={true} />
          </div>
          
          {/* Notes du jour - sur toute la largeur */}
          <div className="mt-3 pt-3 border-t border-slate-700/30">
            <NotesSection 
              notes={conduiteNotes}
              onSave={handleSaveNotes}
              isAmiral={isAmiral}
              isEditing={isEditingNotes}
              setIsEditing={setIsEditingNotes}
              editValue={editNotesValue}
              setEditValue={setEditNotesValue}
              saving={savingNotes}
            />
          </div>
        </div>
        
        {/* GROUPE Section */}
        <SiteSection
          title="Groupe" icon={Building2} sites={groupeSites} publications={publications}
          weekendPublications={weekendPublications} cpPublications={cpPublications} siteCategory="groupe" isAmiral={isAmiral}
          onSiteUpdate={handleSiteUpdate} onRemoveFromConduite={handleRemoveFromConduite}
          onDeleteArticle={handleDeleteArticle} onAddArticle={handleAddArticle}
          onStatusChange={handleStatusChange} onRefreshRss={handleRefreshRss}
          activeArticleId={activeArticleId} allSites={sites}
          bgClass="bg-slate-800/50 border border-slate-600/30" textClass="text-sky-400"
        />
        
        {/* VA Section */}
        <SiteSection
          title="VA" icon={Star} sites={vaSites} publications={publications}
          weekendPublications={[]} cpPublications={[]} siteCategory="va" isAmiral={isAmiral}
          onSiteUpdate={handleSiteUpdate} onRemoveFromConduite={handleRemoveFromConduite}
          onDeleteArticle={handleDeleteArticle} onAddArticle={handleAddArticle}
          onStatusChange={handleStatusChange} onRefreshRss={handleRefreshRss}
          activeArticleId={activeArticleId} allSites={sites}
          bgClass="bg-slate-800/40 border border-slate-600/30" textClass="text-slate-300"
        />
        
        {/* AUTRES Section */}
        <SiteSection
          title="Autres" icon={FileText} sites={autreSites} publications={publications}
          weekendPublications={[]} cpPublications={[]} siteCategory="autre" isAmiral={isAmiral}
          onSiteUpdate={handleSiteUpdate} onRemoveFromConduite={handleRemoveFromConduite}
          onDeleteArticle={handleDeleteArticle} onAddArticle={handleAddArticle}
          onStatusChange={handleStatusChange} onRefreshRss={handleRefreshRss}
          activeArticleId={activeArticleId} allSites={sites}
          bgClass="bg-slate-800/30 border border-slate-700/30" textClass="text-slate-400"
        />
        
        {/* Bottom sections - Tribunes sur toute la largeur */}
        <div className="mt-6">
          <TribunesSection 
            tribunePublications={tribunePublications} 
            onStatusChange={handleStatusChange}
            onDeleteTribune={handleDeleteTribune}
            onAddTribune={handleAddTribune}
            onUpdateUrl={handleUpdateTribuneUrl}
            sites={sites}
          />
        </div>
        
        {sites.length === 0 && !loading && (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">Aucun site configuré</h3>
          </div>
        )}
      </div>
      
      {/* Drag Overlay */}
      <DragOverlay>
        {activeId && activeId.toString().startsWith('article-') && (
          <div className="bg-sky-900/90 text-sky-100 px-3 py-2 rounded-lg shadow-xl text-sm font-medium border border-sky-500">
            📄 Déplacement en cours...
          </div>
        )}
        {activeId && activeId.toString().startsWith('site-') && (
          <div className="bg-violet-900/90 text-violet-100 px-3 py-2 rounded-lg shadow-xl text-sm font-medium border border-violet-500">
            📦 Réorganisation du site...
          </div>
        )}
      </DragOverlay>
      
      {/* Dialog Configuration Alertes Horaires (Amiraux) */}
      <Dialog open={showAlertConfigDialog} onOpenChange={setShowAlertConfigDialog}>
        <DialogContent className="bg-slate-950/98 border-2 border-amber-500/50 shadow-[0_0_50px_rgba(251,191,36,0.2)] max-w-md rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader className="border-b border-amber-500/30 pb-4">
            <DialogTitle className="text-amber-400 flex items-center gap-3 font-semibold text-lg">
              <div className="p-2 bg-amber-500/20 rounded-lg border border-amber-500/40">
                <Bell className="w-5 h-5" />
              </div>
              Configuration des Alertes
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm mt-2">
              Paramétrez les alertes de production pour tous les utilisateurs
            </DialogDescription>
          </DialogHeader>

          {alertConfigLoading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : alertConfig ? (
            <div className="py-4 space-y-5">
              {/* Activation générale */}
              <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-slate-200">Alertes activées</div>
                  <div className="text-xs text-slate-400 mt-0.5">Afficher les alertes de production</div>
                </div>
                <button
                  onClick={() => setAlertConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    alertConfig.enabled ? 'bg-emerald-600' : 'bg-slate-700'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    alertConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Plage horaire */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-slate-200">Plage horaire</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Début</label>
                    <select
                      value={alertConfig.start_hour}
                      onChange={(e) => setAlertConfig(prev => ({ ...prev, start_hour: parseInt(e.target.value) }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-slate-500 pt-5">→</div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Fin</label>
                    <select
                      value={alertConfig.end_hour}
                      onChange={(e) => setAlertConfig(prev => ({ ...prev, end_hour: parseInt(e.target.value) }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Les alertes n'apparaîtront qu'entre ces heures (heure Paris)
                </div>
              </div>

              {/* Week-end */}
              <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-slate-200">Alertes le week-end</div>
                  <div className="text-xs text-slate-400 mt-0.5">Samedi et dimanche inclus</div>
                </div>
                <button
                  onClick={() => setAlertConfig(prev => ({ ...prev, weekend_enabled: !prev.weekend_enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    alertConfig.weekend_enabled ? 'bg-emerald-600' : 'bg-slate-700'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    alertConfig.weekend_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Intervalle */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-slate-200">Intervalle d'apparition</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 60, label: '1h' },
                    { value: 120, label: '2h' },
                    { value: 180, label: '3h' },
                    { value: 240, label: '4h' }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setAlertConfig(prev => ({ ...prev, interval_minutes: opt.value }))}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        alertConfig.interval_minutes === opt.value
                          ? 'bg-amber-600 text-white border-2 border-amber-400'
                          : 'bg-slate-800 text-slate-300 border border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Fréquence des rappels pour chaque utilisateur
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="border-t border-amber-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowAlertConfigDialog(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={() => saveAlertConfig(alertConfig)}
                disabled={savingAlertConfig || !alertConfig}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold"
              >
                {savingAlertConfig ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Purge Confirmation 1 */}
      <Dialog open={showPurgeDialog1} onOpenChange={setShowPurgeDialog1}>
        <DialogContent className="bg-slate-950/98 border-2 border-red-500/50 shadow-[0_0_50px_rgba(239,68,68,0.2)] max-w-md rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader className="border-b border-red-500/30 pb-4">
            <DialogTitle className="text-red-400 flex items-center gap-3 font-semibold text-lg">
              <div className="p-2 bg-red-500/20 rounded-lg border border-red-500/40">
                <AlertCircle className="w-5 h-5" />
              </div>
              Purger la Conduite
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm mt-2">
              Vous êtes sur le point de supprimer <strong className="text-red-400">TOUS les articles</strong> de la conduite éditoriale.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <p className="text-slate-300 mb-2">Cette action est <strong className="text-red-400">irréversible</strong>.</p>
            <p className="text-slate-400 text-sm">Tous les articles du jour, week-end et communiqués seront supprimés.</p>
          </div>
          
          <DialogFooter className="border-t border-red-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowPurgeDialog1(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={() => {
                  setShowPurgeDialog1(false);
                  setShowPurgeDialog2(true);
                }}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-semibold"
              >
                Continuer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Purge Confirmation 2 (finale) */}
      <Dialog open={showPurgeDialog2} onOpenChange={setShowPurgeDialog2}>
        <DialogContent className="bg-slate-950/98 border-2 border-red-600 shadow-[0_0_60px_rgba(239,68,68,0.3)] max-w-md rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader className="border-b border-red-500/30 pb-4">
            <DialogTitle className="text-red-500 flex items-center gap-3 font-bold text-lg uppercase tracking-wider">
              <div className="p-2 bg-red-600/30 rounded-lg border border-red-500 animate-pulse">
                <Trash2 className="w-5 h-5" />
              </div>
              Confirmation Finale
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 text-center">
            <div className="bg-red-950/50 border border-red-500/50 rounded-lg p-4 mb-4">
              <p className="text-red-300 font-semibold text-lg mb-2">ÊTES-VOUS ABSOLUMENT SÛR ?</p>
              <p className="text-slate-400 text-sm">
                Cliquez sur "PURGER MAINTENANT" pour supprimer définitivement tous les articles.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Comptez mentalement jusqu'à 3 avant de cliquer si vous hésitez...
            </p>
          </div>
          
          <DialogFooter className="border-t border-red-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowPurgeDialog2(false)}
              >
                Non, annuler
              </Button>
              <Button
                onClick={handlePurgeConduite}
                disabled={purging}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider"
                data-testid="confirm-purge-btn"
              >
                {purging ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {purging ? 'Suppression...' : 'Purger Maintenant'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Brave Search Confirmation */}
      <Dialog open={showBraveSearchDialog} onOpenChange={setShowBraveSearchDialog}>
        <DialogContent className="bg-slate-950/98 border border-sky-600/50 shadow-[0_0_40px_rgba(14,165,233,0.2)] max-w-md rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader className="border-b border-sky-500/30 pb-4">
            <DialogTitle className="text-sky-400 flex items-center gap-3 font-bold text-lg">
              <div className="p-2 bg-sky-600/20 rounded-lg border border-sky-500/50">
                <Search className="w-5 h-5" />
              </div>
              Vérification Brave Search
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-sky-950/30 border border-sky-500/30 rounded-lg p-4 mb-4">
              <p className="text-slate-300 text-sm mb-3">
                Cette fonction va rechercher les <strong>articles en brouillon et oubliés</strong> présents 
                dans la conduite actuelle via Brave Search pour tenter de les valider automatiquement.
              </p>
              <div className="flex items-start gap-2 text-xs text-amber-400/80">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Attention :</strong> Chaque article vérifié consomme 1 crédit Brave Search. 
                  Limite mensuelle : 1000 crédits.
                </span>
              </div>
            </div>
            
            {braveSearchResult && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-xs">
                <p className="text-slate-400 mb-1">Dernier résultat :</p>
                <p className="text-emerald-400">
                  {braveSearchResult.publications_confirmed} validé(s) / {braveSearchResult.publications_checked} vérifié(s)
                </p>
                <p className="text-slate-500">
                  Crédits restants : {braveSearchResult.credits_remaining || '?'}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-sky-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowBraveSearchDialog(false)}
                disabled={braveSearching}
              >
                Annuler
              </Button>
              <Button
                onClick={handleBraveSearch}
                disabled={braveSearching}
                className="flex-1 bg-sky-600 hover:bg-sky-700 text-white font-medium"
                data-testid="confirm-brave-search-btn"
              >
                {braveSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Lancer la vérification
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Popup d'alerte horaire de production */}
      <HourlyAlertPopup enabled={true} />
    </DndContext>
  );
};

export default ConduitePage;
